import React, { useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { inr } from '@/lib/tagPricing';
import { returnExcel } from './TagReturnDialog';
import { TagMultiFilter } from './TagMultiFilter';
import { TagThumbStrip } from './TagThumbStrip';
import { orderPieces, useTypeOrder } from '@/lib/tagGroups';

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-GB') : '');

function ReturnRow({ r, thumbs }) {
  const [open, setOpen] = useState(false);
  useTypeOrder();
  const items = orderPieces(r.items, (i) => i.page_no, (i) => i.jewellery_no, (i) => i.jewellery_type);
  return (
    <div className="rounded-2xl border border-border bg-card" data-testid="tagws-return-row">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left" data-testid="tagws-return-row-toggle">
        <span className="font-mono text-sm font-bold text-amber-700" data-testid="tagws-return-row-number">{r.number}</span>
        <span className="text-sm font-semibold text-foreground">{r.customer_names.join(', ')}</span>
        <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)} · {r.items.length} piece{r.items.length === 1 ? '' : 's'} · sold at Rs. {inr(r.sale_total)}</span>
        {r.stock_customer_name ? <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid="tagws-return-row-stock">Back under {r.stock_customer_name}</span> : null}
        <span className="ml-auto font-mono text-sm font-semibold text-foreground">Taken back Rs. {inr(r.total)} <span className="text-xs font-normal text-amber-700">(− Rs. {inr(r.sale_total - r.total)})</span></span>
      </button>
      {open ? (
        <div className="space-y-3 border-t border-border p-3">
          <TagThumbStrip items={items} thumbs={thumbs} testid="tagws-return-photos" />
          <table className="w-full text-xs">
            <thead className="text-left text-[length:calc(var(--fs,1)*0.65625rem)] uppercase tracking-wider text-muted-foreground"><tr><th className="py-1">Jewellery no.</th><th>Sold in</th><th>Purity</th><th className="text-right">Gross</th><th className="text-right">Sold at</th><th className="text-right">Returned at</th><th className="text-right">Deduction</th></tr></thead>
            <tbody>{items.map((i) => (
              <tr key={i.id} className="border-t border-border/60">
                <td className="py-1.5 font-mono font-semibold text-emerald-700">{i.jewellery_no}</td><td>{i.sale_number} · {i.customer_name}</td><td>{i.purity}</td>
                <td className="text-right font-mono">{i.gross.toFixed(3)}</td><td className="text-right font-mono">{inr(i.sale_total)}</td><td className="text-right font-mono font-semibold">{inr(i.total)}</td><td className="text-right font-mono text-amber-700">{inr(i.sale_total - i.total)}</td>
              </tr>))}</tbody>
          </table>
          <button type="button" onClick={() => returnExcel(r, thumbs).catch(() => toast.error('Could not build the Excel file.'))} className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-border bg-background px-3 text-xs font-semibold text-emerald-700 hover:bg-secondary" data-testid="tagws-return-row-excel"><FileSpreadsheet size={13} /> Excel</button>
        </div>
      ) : null}
    </div>
  );
}

// Sales returns of the period, with customer filter.
export function TagReturnsList({ returns, thumbs }) {
  const [cust, setCust] = useState([]);
  const all = [...new Set(returns.flatMap((r) => r.customer_names))].sort();
  const on = cust.filter((c) => all.includes(c));
  const shown = on.length ? returns.filter((r) => r.customer_names.some((c) => on.includes(c))) : returns;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3" data-testid="tagws-returns-list">
      <div className="flex flex-wrap items-center gap-2">
        <TagMultiFilter label="Customers" allLabel="All customers" values={all} value={on} onChange={setCust} testid="tagws-returns-filter-cust" />
        <span className="ml-auto text-xs text-muted-foreground" data-testid="tagws-returns-count">{shown.length} return{shown.length === 1 ? '' : 's'} · taken back Rs. {inr(shown.reduce((t, r) => t + r.total, 0))}</span>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
        {shown.map((r) => <ReturnRow key={r.id} r={r} thumbs={thumbs} />)}
        {!shown.length ? <p className="py-10 text-center text-sm text-muted-foreground">No sales returns in this period.</p> : null}
      </div>
    </div>
  );
}
